import React, { useEffect, useState } from "react";
import axios from "axios";
import { Gauge, Truck, Navigation, Clock, CornerUpRight, ChevronDown, ChevronUp } from "lucide-react";
import { apiBaseUrl, authHeaders } from "./shared";

// Miles reconciliation: Loaded (delivered vs in-transit) + Deadhead + Idle vs Odometer (ground truth).
// Deadhead is now its OWN bucket (was hidden inside "loaded"); loaded is split into delivered trips
// (odometer has really driven them) vs in-transit trips (full planned route counted up front though
// only partly driven -- the main reason tracked > odometer). Idle is the CANONICAL episode-aware
// figure, never the raw-sum overcount.

const fmt = (n) => (n == null ? "—" : Math.round(n).toLocaleString("en-US"));
const BRAND = "var(--brand)";
const TRANSIT_C = "#6366f1"; // indigo -- loaded but not yet driven
const DH_C = "#64748b";      // slate -- deadhead
const IDLE_C = "#d97706";    // amber
const ODO_C = "#0f766e";     // teal

const fmtRange = (s, e) => {
  try {
    const o = { month: "short", day: "numeric" };
    return `${new Date(s + "T00:00:00").toLocaleDateString("en-US", o)} – ${new Date(e + "T00:00:00").toLocaleDateString("en-US", o)}`;
  } catch { return ""; }
};

// Pass start/end (YYYY-MM-DD) to track the Reports date filter; falls back to a
// rolling last-`days` window when they're absent.
export default function MilesReconciliation({ days = 7, start = null, end = null }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const ranged = Boolean(start && end);

  useEffect(() => {
    let alive = true;
    setData(null); setErr(false);
    const params = ranged ? { start: `${start}T00:00:00`, end: `${end}T23:59:59` } : { days };
    axios
      .get(`${apiBaseUrl}/fleet/miles-report`, { ...authHeaders(), params })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [days, start, end, ranged]);

  const t = data?.totals;
  const scale = t ? Math.max(t.driven_aivan, t.odometer, 1) : 1;
  const gapPct = t?.driven_gap_pct_of_odometer;
  const gapAbs = gapPct == null ? 0 : Math.abs(gapPct);
  const gapColor = gapAbs <= 5 ? "#16a34a" : gapAbs <= 15 ? "#d97706" : "#dc2626";

  return (
    <div style={styles.card}>
      <div style={styles.head}>
        <div style={styles.title}>Miles <span style={styles.sub}>· {ranged ? fmtRange(start, end) : `last ${days} days`}</span></div>
        {t && (
          <span style={{ ...styles.gapPill, color: gapColor, borderColor: gapColor + "55", background: gapColor + "11" }}>
            {t.driven_gap >= 0 ? "+" : ""}{fmt(t.driven_gap)} mi vs odometer{gapPct != null ? ` (${gapPct > 0 ? "+" : ""}${gapPct}%)` : ""}
          </span>
        )}
      </div>

      {err && <div style={styles.note}>Couldn’t load miles report.</div>}
      {!err && !data && <div style={styles.note}>Loading…</div>}

      {t && (
        <>
          <div style={styles.headline}>
            <span style={styles.headlineValue}>{fmt(t.driven_aivan)}</span>
            <span style={styles.headlineUnit}>mi driven (AIVAN)</span>
            <span style={styles.headlineVs}>
              ({fmt(t.not_yet_driven)} loaded not yet driven) · odometer {fmt(t.odometer)} mi
            </span>
          </div>

          {/* comparison: AIVAN miles DRIVEN so far (delivered + in-transit driven +
              deadhead + idle) vs odometer -- these should roughly agree. The
              not-yet-driven booked remainder is shown separately, not in the bar. */}
          <div style={styles.bars}>
            <div style={styles.barRow}>
              <span style={styles.barLabel}>AIVAN driven</span>
              <div style={styles.track}>
                <div style={{ width: `${(t.loaded_delivered / scale) * 100}%`, background: BRAND }} title={`Loaded delivered ${fmt(t.loaded_delivered)} mi`} />
                <div style={{ width: `${(t.loaded_in_transit_driven / scale) * 100}%`, background: TRANSIT_C }} title={`In-transit driven so far ${fmt(t.loaded_in_transit_driven)} mi`} />
                <div style={{ width: `${(t.deadhead / scale) * 100}%`, background: DH_C }} title={`Deadhead ${fmt(t.deadhead)} mi`} />
                <div style={{ width: `${(t.idle / scale) * 100}%`, background: IDLE_C }} title={`Idle ${fmt(t.idle)} mi`} />
              </div>
            </div>
            <div style={styles.barRow}>
              <span style={styles.barLabel}>Odometer</span>
              <div style={styles.track}>
                <div style={{ width: `${(t.odometer / scale) * 100}%`, background: ODO_C }} title={`Odometer ${fmt(t.odometer)} mi`} />
              </div>
            </div>
          </div>

          {/* tiles */}
          <div style={styles.tiles}>
            <Tile icon={<Navigation size={14} color={BRAND} />} color={BRAND} label="Loaded · delivered" value={fmt(t.loaded_delivered)}
                  foot={t.loaded_delivered_pct != null ? `${t.loaded_delivered_pct}% of tracked · finished trips` : "finished trips"} />
            <Tile icon={<Clock size={14} color={TRANSIT_C} />} color={TRANSIT_C} label="Loaded · in-transit" value={fmt(t.loaded_in_transit)}
                  foot={`booked · driven ${fmt(t.loaded_in_transit_driven)} tracked${t.in_transit_loads ? ` · ${t.in_transit_loads_tracked}/${t.in_transit_loads} loads` : ""}`} />
            <Tile icon={<CornerUpRight size={14} color={DH_C} />} color={DH_C} label="Deadhead" value={fmt(t.deadhead)}
                  foot={t.deadhead_pct != null ? `${t.deadhead_pct}% of tracked · empty to pickup` : "empty to pickup"} />
            <Tile icon={<Truck size={14} color={IDLE_C} />} color={IDLE_C} label="Idle" value={fmt(t.idle)}
                  foot={t.idle_pct != null ? `${t.idle_pct}% of tracked · canonical` : "canonical"} />
            <Tile icon={<Gauge size={14} color={ODO_C} />} color={ODO_C} label="Odometer" value={fmt(t.odometer)}
                  foot={`ground truth · ${t.trucks_with_odometer}/${t.trucks_total} trucks`} />
          </div>

          <button style={styles.toggle} onClick={() => setShowTable((s) => !s)}>
            {showTable ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Per-truck breakdown
          </button>
          {showTable && (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Truck</th>
                    <th style={styles.thR}>Delivered</th>
                    <th style={styles.thR}>In-transit</th>
                    <th style={styles.thR}>Deadhead</th>
                    <th style={styles.thR}>Idle</th>
                    <th style={styles.thR}>Odometer</th>
                    <th style={styles.thR}>Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_truck.slice(0, 40).map((r) => (
                    <tr key={r.truck_id}>
                      <td style={styles.td}>{r.unit_number}</td>
                      <td style={styles.tdR}>{fmt(r.loaded_delivered)}</td>
                      <td style={{ ...styles.tdR, color: TRANSIT_C }}>{fmt(r.loaded_in_transit)}</td>
                      <td style={styles.tdR}>{fmt(r.deadhead)}</td>
                      <td style={styles.tdR}>{fmt(r.idle)}</td>
                      <td style={styles.tdR}>{r.has_odometer ? fmt(r.odometer) : "—"}</td>
                      <td style={{ ...styles.tdR, color: !r.has_odometer ? "var(--muted)" : r.gap < 0 ? "#dc2626" : "var(--ink)" }}>
                        {r.has_odometer ? `${r.gap >= 0 ? "+" : ""}${fmt(r.gap)}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={styles.foot}>
                Loaded split: delivered = finished trips (odometer has driven them) · in-transit =
                picked up but not delivered, so the full planned route is counted even though only
                PART has been driven so far — the not-yet-driven remainder is essentially the whole
                tracked-vs-odometer gap. Deadhead = empty drive to pickup (own bucket). Idle =
                canonical episode-aware movement. Odometer = max−min telematics (ground truth).
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Tile({ icon, color, label, value, foot }) {
  return (
    <div style={styles.tile}>
      <div style={styles.tileHead}>{icon}<span style={{ ...styles.tileLabel, color }}>{label}</span></div>
      <div style={styles.tileValue}>{value}<span style={styles.tileUnit}> mi</span></div>
      <div style={styles.tileFoot}>{foot}</div>
    </div>
  );
}

const styles = {
  card: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: 18 },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" },
  title: { fontSize: 14, fontWeight: 800, color: "var(--ink)" },
  sub: { fontSize: 12, fontWeight: 500, color: "var(--muted)" },
  gapPill: { fontSize: 11, fontWeight: 700, border: "1px solid", borderRadius: 999, padding: "3px 9px", fontVariantNumeric: "tabular-nums" },
  note: { fontSize: 13, color: "var(--muted)", padding: "16px 0" },

  headline: { display: "flex", alignItems: "baseline", gap: 8, marginTop: 14, flexWrap: "wrap" },
  headlineValue: { fontSize: 30, fontWeight: 800, color: "var(--ink)", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" },
  headlineUnit: { fontSize: 13, fontWeight: 600, color: "var(--muted)" },
  headlineVs: { fontSize: 11.5, color: "var(--muted)", marginLeft: "auto" },

  bars: { display: "flex", flexDirection: "column", gap: 8, marginTop: 16 },
  barRow: { display: "flex", alignItems: "center", gap: 10 },
  barLabel: { fontSize: 11, fontWeight: 700, color: "var(--muted)", width: 64, textAlign: "right", textTransform: "uppercase", letterSpacing: 0.5 },
  track: { flex: 1, height: 16, borderRadius: 6, overflow: "hidden", display: "flex", background: "var(--bg)", border: "1px solid var(--line)" },

  tiles: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginTop: 18 },
  tile: { background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 10, padding: "11px 12px" },
  tileHead: { display: "flex", alignItems: "center", gap: 6 },
  tileLabel: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5 },
  tileValue: { fontSize: 21, fontWeight: 800, color: "var(--ink)", marginTop: 4, fontVariantNumeric: "tabular-nums" },
  tileUnit: { fontSize: 12, fontWeight: 600, color: "var(--muted)" },
  tileFoot: { fontSize: 10.5, color: "var(--muted)", marginTop: 3 },

  toggle: { display: "flex", alignItems: "center", gap: 5, marginTop: 16, background: "none", border: "none", cursor: "pointer", color: "var(--brand)", fontSize: 12, fontWeight: 700, padding: 0 },
  tableWrap: { marginTop: 10, overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: { textAlign: "left", padding: "6px 8px", color: "var(--muted)", fontWeight: 700, borderBottom: "1px solid var(--line)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 },
  thR: { textAlign: "right", padding: "6px 8px", color: "var(--muted)", fontWeight: 700, borderBottom: "1px solid var(--line)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4 },
  td: { padding: "6px 8px", color: "var(--ink)", fontWeight: 600, borderBottom: "1px solid var(--line)" },
  tdR: { padding: "6px 8px", textAlign: "right", color: "var(--ink)", borderBottom: "1px solid var(--line)", fontVariantNumeric: "tabular-nums" },
  foot: { fontSize: 10.5, color: "var(--muted)", marginTop: 10, lineHeight: 1.5 },
};
